import {UseQueryResult} from '@tanstack/react-query';
import {MRT_ColumnDef} from 'material-react-table';

import {
  GetAllPaymentsParams,
  PaymentStatusFilter,
  Transaction,
} from '@entities/transaction';
import {GetListOutput, QueryOptionsForList} from '@shared/api';
import {MrTable} from '@shared/components';
import {FilterRow, SelectFilter} from '@shared/components/filters';
import {entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';

import {PaginationState} from '@/shared/types';

const statusFilterOptions = [
  {value: null, label: 'Status'},
  {value: PaymentStatusFilter.success, label: 'Success'},
  {value: PaymentStatusFilter.pending, label: 'Pending'},
  {value: PaymentStatusFilter.failed, label: 'Failed'},
];

type UrlState = PaginationState & {
  filter: PaymentStatusFilter | null;
};

type UseGetPayments = (
  params: GetAllPaymentsParams,
  options?: QueryOptionsForList<Transaction, GetListOutput<Transaction>>,
) => UseQueryResult<GetListOutput<Transaction>, Error>;

interface Props {
  useGetPayments: UseGetPayments;
  columns: MRT_ColumnDef<Transaction>[];
  localStorageKey: string;
}

/**
 * Deposits and withdrawals differ only by the endpoint they read from
 * and the columns (details route) they use, so they share this table.
 */
export const AllPaymentsTab = ({
  useGetPayments,
  columns,
  localStorageKey,
}: Props) => {
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
    filter: null,
  });

  const {data, isFetching, error} = useGetPayments({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
    filter: urlState.filter ?? PaymentStatusFilter.all,
  });

  return (
    <MrTable
      columns={columns}
      data={data?.items ?? []}
      error={error}
      renderTopToolbarCustomActions={() => (
        <FilterRow>
          <SelectFilter
            value={urlState.filter}
            onChange={filter =>
              setUrl(prev => ({...prev, pageIndex: 0, filter}))
            }
            options={statusFilterOptions}
          />
        </FilterRow>
      )}
      enableColumnOrdering
      enableColumnDragging={false}
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      /////////////////////////////
      // pagination + state
      enablePagination
      manualPagination
      onPaginationChange={updater =>
        setUrl(prev => ({
          ...prev,
          ...(typeof updater === 'function' ? updater(prev) : updater),
        }))
      }
      rowCount={data?.total ?? 0}
      /////////////////////////////
      state={{
        isLoading: isFetching,
        pagination: {
          pageIndex: urlState.pageIndex,
          pageSize: urlState.pageSize,
        },
      }}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
